import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosInstance } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AutoRequeueMode,
    getProposals,
    groupKey,
    IssueRef,
    MODE_HINT,
    MODE_LABEL,
    ProposalGroup,
    REQUEUE_STATE_LABEL,
    RequeueDecisionResult,
    RequeueGroupRef,
    RequeueProposalPage,
    RequeueSummary,
    SweeperStatus,
} from '../../api/issues';
import { describeError } from '../../api/requeue';
import { LoadingBlock, Refreshing } from '../Loading/Loading';
import { age, date, dateAgo } from './issueFormat';

export interface IProposalsPanelProps {
    axios?: AxiosInstance;
    summary: RequeueSummary | null;
    loading: boolean;
    error: string | null;
    status: SweeperStatus | null;
    busy: boolean;
    /** What the last decision came to, in words. */
    message: string | null;
    results: RequeueDecisionResult[] | null;
    onMode: (mode: AutoRequeueMode | null) => void;
    onApproveGroup: (group: RequeueGroupRef, count: number) => void;
    onRejectGroup: (group: RequeueGroupRef, count: number) => void;
    onCloseGroup: (group: ProposalGroup) => void;
    onApprove: (items: IssueRef[]) => void;
    onReject: (items: IssueRef[]) => void;
}

const PAGE = 50;

const refOf = (g: ProposalGroup): RequeueGroupRef => ({ customer: g.customer, environment: g.environment, code: g.code, lane: g.lane, backlog: g.backlog });

const keyOf = (r: IssueRef) => `${r.customer}/${r.environment}/${r.key}`;

const sum = (groups: ProposalGroup[]) => groups.reduce((total, g) => total + g.count, 0);

/**
 * What the sweep would offer again, in groups per environment and kind of problem: what waits
 * for a person, what goes by itself in portions, and the backlog that never goes by itself.
 * The objects of a group are fetched only when someone opens it.
 */
export const ProposalsPanel = ({
    axios,
    summary,
    loading,
    error,
    status,
    busy,
    message,
    results,
    onMode,
    onApproveGroup,
    onRejectGroup,
    onCloseGroup,
    onApprove,
    onReject,
}: IProposalsPanelProps) => {
    const [filter, setFilter] = useState<string>('');
    const [open, setOpen] = useState<string | null>(null);

    const groups = useMemo(
        () =>
            (summary?.groups ?? []).filter(
                (g) => !filter || `${g.customer}/${g.environment} ${g.title}`.toLowerCase().includes(filter.toLowerCase())
            ),
        [summary, filter]
    );

    const forPerson = groups.filter((g) => !g.backlog && !g.automatic);
    const automatic = groups.filter((g) => !g.backlog && g.automatic);
    const backlog = groups.filter((g) => g.backlog);

    if (summary === null) {
        return error ? <p className="issues__error">{error}</p> : <LoadingBlock label="Voorstellen ophalen…" />;
    }

    const section = (title: string, hint: string, list: ProposalGroup[], kind: 'person' | 'automatic' | 'backlog') => (
        <section className="proposals__section" aria-label={title}>
            <h4 className="proposals__title">
                {title} <span className="issues__muted">· {sum(list).toLocaleString('nl-NL')} in {list.length} groep(en)</span>
            </h4>
            <p className="issues__muted">{hint}</p>
            {list.length === 0 ? (
                <p className="issues__muted">
                    <FontAwesomeIcon icon="circle-check" /> Niets.
                </p>
            ) : (
                <ul className="proposals__groups">
                    {list.map((g) => {
                        const key = groupKey(refOf(g));

                        return (
                            <GroupRow
                                key={key}
                                group={g}
                                kind={kind}
                                busy={busy}
                                expanded={open === key}
                                maxPerRound={summary.maxPerRound}
                                onToggle={() => setOpen(open === key ? null : key)}
                                onApprove={() => onApproveGroup(refOf(g), g.count - g.approved)}
                                onReject={() => onRejectGroup(refOf(g), g.count)}
                                onClose={() => onCloseGroup(g)}
                            >
                                {open === key && axios && (
                                    <GroupItems axios={axios} group={g} busy={busy} onApprove={onApprove} onReject={onReject} />
                                )}
                            </GroupRow>
                        );
                    })}
                </ul>
            )}
        </section>
    );

    return (
        <Refreshing active={loading} label="Voorstellen bijwerken…">
            <section className="issues__notice proposals" id="voorstellen" aria-label="Opnieuw aanbieden">
                <div className="issues__notice-head">
                    <h3 className="issues__heading">
                        <FontAwesomeIcon icon="rotate-left" /> Opnieuw aanbieden
                    </h3>
                    <input
                        className="form-control form-control-sm proposals__filter"
                        type="search"
                        placeholder="Filter op klant, omgeving of probleem"
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                        aria-label="Filter"
                    />
                </div>

                {error && <p className="issues__error">{error}</p>}

                <ModeSwitch summary={summary} status={status} busy={busy} onMode={onMode} />

                {message && <p className="proposals__message">{message}</p>}

                {results && results.length > 0 && (
                    <ul className="sweeper__failing">
                        {results.map((r) => (
                            <li key={keyOf(r.item)}>
                                {r.item.customer} / {r.item.environment} {r.item.key}:{' '}
                                <strong>{r.state === 'Gone' ? 'niet meer voorgesteld' : REQUEUE_STATE_LABEL[r.state] ?? r.state}</strong>
                                {r.note && <span className="issues__muted"> · {r.note}</span>}
                            </li>
                        ))}
                    </ul>
                )}

                {section(
                    'Wacht op jouw goedkeuring',
                    'Recente problemen waarbij een nieuwe poging iets boekt, zoals een betaling. Goedkeuren zet de hele groep klaar; de scanner biedt ze in porties aan, door dezelfde controles als de knop in het foutenlogboek.',
                    forPerson,
                    'person'
                )}
                {section(
                    'Gaat vanzelf',
                    `Deze biedt de scanner zelf opnieuw aan: elke ronde hooguit ${summary.maxPerRound} per omgeving, alleen als Exact ruimte heeft, en per object hooguit ${summary.maxRequeues} keer met steeds langer wachten. Niets te doen, tenzij je iets wilt tegenhouden.`,
                    automatic,
                    'automatic'
                )}
                {section(
                    `Achterstand: eerste fout langer dan ${summary.maxAgeDays} dagen geleden`,
                    'Gaat nooit vanzelf: een boeking van maanden terug kan in een afgesloten periode vallen, of is intussen met de hand geboekt. Kijk per groep of het nog moet. Goedkeuren zet de groep klaar voor de porties; sluiten haalt hem uit de lijst tot het object opnieuw faalt.',
                    backlog,
                    'backlog'
                )}
            </section>
        </Refreshing>
    );
};

const ModeSwitch = ({
    summary,
    status,
    busy,
    onMode,
}: {
    summary: RequeueSummary;
    status: SweeperStatus | null;
    busy: boolean;
    onMode: (mode: AutoRequeueMode | null) => void;
}) => (
    <div className="proposals__mode">
        <label>
            <span>Automatisch opnieuw aanbieden</span>
            <select
                className="form-select form-select-sm"
                value={summary.mode}
                disabled={busy}
                onChange={(event) => {
                    const next = event.target.value as AutoRequeueMode;

                    if (window.confirm(`Opnieuw aanbieden zetten op "${MODE_LABEL[next]}"?\n\n${MODE_HINT[next]}\n\nGeldt vanaf de volgende scan van elke omgeving.`)) {
                        onMode(next);
                    }
                }}
            >
                {(Object.keys(MODE_LABEL) as AutoRequeueMode[]).map((mode) => (
                    <option key={mode} value={mode}>
                        {MODE_LABEL[mode]}
                    </option>
                ))}
            </select>
        </label>
        <span className="issues__muted">
            {MODE_HINT[summary.mode]}
            {status?.autoRequeueChosen
                ? ` Gekozen door ${status.autoRequeueChangedBy ?? 'onbekend'} op ${date(status.autoRequeueChangedAt)}.`
                : ' Standaardinstelling.'}
            {!summary.allowRequeue && ' Deze API doet alleen proefdraaien: er wordt niets verstuurd.'}
            {status && !status.enabled && ' Let op: de timer van de scanner staat uit, dus er gaan geen porties.'}
            {status?.pause?.paused && ' Let op: de scanner staat op pauze, dus er gaan geen porties.'}
        </span>
        {status?.autoRequeueChosen && (
            <button type="button" className="btn btn-sm btn-link p-0" disabled={busy} onClick={() => onMode(null)}>
                Terug naar standaard ({MODE_LABEL[status.autoRequeueConfigured]})
            </button>
        )}
    </div>
);

const GroupRow = ({
    group: g,
    kind,
    busy,
    expanded,
    maxPerRound,
    onToggle,
    onApprove,
    onReject,
    onClose,
    children,
}: {
    group: ProposalGroup;
    kind: 'person' | 'automatic' | 'backlog';
    busy: boolean;
    expanded: boolean;
    maxPerRound: number;
    onToggle: () => void;
    onApprove: () => void;
    onReject: () => void;
    onClose: () => void;
    children?: ReactNode;
}) => {
    const waiting = g.count - g.approved;
    // Rounds needed at the portion size, as a rough idea of how long it takes.
    const rounds = Math.ceil((kind === 'automatic' ? g.count : g.approved) / Math.max(1, maxPerRound));

    return (
        <li className={`proposals__group${expanded ? ' is-open' : ''}`}>
            <div className="proposals__row">
                <button type="button" className="proposals__toggle" onClick={onToggle} aria-expanded={expanded}>
                    <FontAwesomeIcon icon={expanded ? 'angle-down' : 'angle-right'} />
                    <span className="proposals__what">
                        <strong>
                            {g.customer} / {g.environment}
                        </strong>{' '}
                        {g.title}
                        {g.action && <span className="issues__type">{g.action}</span>}
                    </span>
                </button>
                <span className="proposals__count">{g.count.toLocaleString('nl-NL')}</span>
                <span className="proposals__age issues__muted" title={`Eerste fout ${dateAgo(g.oldestAt)}, laatste ${dateAgo(g.latestAt)}`}>
                    oudste {age(g.oldestAt)}
                </span>
                <span className="issues__actions">
                    {kind !== 'automatic' && waiting > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={busy}
                            onClick={onApprove}
                            title="Zet de hele groep klaar; de scanner biedt ze aan in porties, als Exact ruimte heeft"
                        >
                            <FontAwesomeIcon icon="check" /> Goedkeuren ({waiting.toLocaleString('nl-NL')})
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy}
                        onClick={onReject}
                        title="Niet opnieuw aanbieden; komt pas terug als het object opnieuw faalt"
                    >
                        <FontAwesomeIcon icon="xmark" /> {kind === 'automatic' ? 'Tegenhouden' : 'Afwijzen'}
                    </button>
                    {kind === 'backlog' && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={busy}
                            onClick={onClose}
                            title="Sluit deze issues: ze doen er niet meer toe. Ze gaan pas weer open als het object opnieuw faalt."
                        >
                            <FontAwesomeIcon icon="box-archive" /> Sluiten
                        </button>
                    )}
                </span>
            </div>
            <div className="proposals__notes">
                {g.approved > 0 && (
                    <span className="proposals__note proposals__note--ok">
                        <FontAwesomeIcon icon="check" /> {g.approved.toLocaleString('nl-NL')} goedgekeurd, nog ongeveer {rounds} ronde(s)
                    </span>
                )}
                {kind === 'automatic' && (
                    <span className="proposals__note">
                        <FontAwesomeIcon icon="bolt" /> gaat vanzelf, nog ongeveer {rounds} ronde(s)
                    </span>
                )}
                {!g.inScope && (g.approved > 0 || kind === 'automatic') && (
                    <span className="proposals__note proposals__note--warn">
                        <FontAwesomeIcon icon="triangle-exclamation" /> de timer scant deze omgeving niet: alleen &quot;Nu scannen&quot; bij de
                        omgeving stuurt een portie
                    </span>
                )}
                {g.held && (g.approved > 0 || kind === 'automatic') && (
                    <span className="proposals__note proposals__note--warn">
                        <FontAwesomeIcon icon="hourglass-half" /> wacht: {g.held}
                    </span>
                )}
            </div>
            {children}
        </li>
    );
};

/**
 * The objects of one group, a page at a time, to approve or reject a few of them.
 */
const GroupItems = ({
    axios,
    group,
    busy,
    onApprove,
    onReject,
}: {
    axios: AxiosInstance;
    group: ProposalGroup;
    busy: boolean;
    onApprove: (items: IssueRef[]) => void;
    onReject: (items: IssueRef[]) => void;
}) => {
    const [skip, setSkip] = useState<number>(0);
    const [page, setPage] = useState<RequeueProposalPage | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [picked, setPicked] = useState<Set<string>>(new Set());

    const load = useCallback(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(null);

        getProposals(axios, refOf(group), skip, PAGE, controller.signal)
            .then(setPage)
            .catch((ex) => {
                if (!controller.signal.aborted) {
                    setError(describeError(ex));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [axios, group, skip]);

    useEffect(load, [load]);

    const items = page?.items ?? [];
    const refs = items.map((p) => ({ customer: p.issue.customer, environment: p.issue.environment, key: p.issue.key }));
    const chosen = refs.filter((r) => picked.has(keyOf(r)));
    const all = chosen.length === refs.length && refs.length > 0;

    const toggle = (key: string) => {
        const next = new Set(picked);

        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }

        setPicked(next);
    };

    if (page === null) {
        return error ? <p className="issues__error">{error}</p> : <LoadingBlock compact label="Objecten van deze groep ophalen…" />;
    }

    return (
        <Refreshing active={loading} label="Volgende pagina ophalen…">
            <div className="proposals__items">
                {error && <p className="issues__error">{error}</p>}
                <div className="issues__actions">
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy || chosen.length === 0}
                        onClick={() => {
                            onApprove(chosen);
                            setPicked(new Set());
                        }}
                        title="Tot 25 tegelijk gaan meteen op de queue; meer worden klaargezet voor de porties"
                    >
                        <FontAwesomeIcon icon="check" /> Gekozen goedkeuren ({chosen.length})
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy || chosen.length === 0}
                        onClick={() => {
                            onReject(chosen);
                            setPicked(new Set());
                        }}
                    >
                        <FontAwesomeIcon icon="xmark" /> Gekozen afwijzen
                    </button>
                    <span className="issues__muted">
                        {page.total === 0 ? 'geen' : `${skip + 1}-${Math.min(skip + PAGE, page.total)} van ${page.total.toLocaleString('nl-NL')}`}
                    </span>
                    <button type="button" className="btn btn-sm btn-link" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE))}>
                        <FontAwesomeIcon icon="angle-left" /> Vorige
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-link"
                        disabled={skip + PAGE >= page.total || loading}
                        onClick={() => setSkip(skip + PAGE)}
                    >
                        Volgende <FontAwesomeIcon icon="angle-right" />
                    </button>
                </div>
                <div className="issues__scroll">
                    <table className="issues__table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={all}
                                        onChange={() => setPicked(all ? new Set() : new Set(refs.map(keyOf)))}
                                        aria-label="Hele pagina"
                                    />
                                </th>
                                <th>Object</th>
                                <th className="issues__num">Pogingen</th>
                                <th className="issues__num">Al aangeboden</th>
                                <th>Eerste fout</th>
                                <th>Laatste fout</th>
                                <th>Stand</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => {
                                const key = keyOf({ customer: p.issue.customer, environment: p.issue.environment, key: p.issue.key });

                                return (
                                    <tr key={key} className="is-clickable" onClick={() => toggle(key)}>
                                        <td>
                                            <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} onClick={(e) => e.stopPropagation()} />
                                        </td>
                                        <td>
                                            <span className="issues__id">{p.issue.relationId}</span>
                                            <span className="issues__type">
                                                {p.issue.objectType}
                                                {p.issue.invoiceId && p.issue.invoiceId !== p.issue.relationId ? ` · factuur ${p.issue.invoiceId}` : ''}
                                            </span>
                                        </td>
                                        <td className="issues__num">{p.issue.attempts}</td>
                                        <td className="issues__num">{p.offered}×</td>
                                        <td>{date(p.issue.firstFailedAt)}</td>
                                        <td>{dateAgo(p.issue.lastFailedAt)}</td>
                                        <td>
                                            {p.issue.autoRequeueState ? REQUEUE_STATE_LABEL[p.issue.autoRequeueState] : ''}
                                            {p.issue.autoRequeueNote && <span className="issues__type">{p.issue.autoRequeueNote}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </Refreshing>
    );
};
